import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import {
  device,
  typography,
  sizes,
  container,
  inViewConfig,
  shouldForwardProp,
} from '@landsbankinn/ui';
import React, { Children } from 'react';

import { useInView } from 'react-intersection-observer';
import { useSpring, animated } from 'react-spring';

import { Aukakronur } from '../../components/icons/Aukakronur';
import { Img } from '../../components/img/Img';
import { VerticalReel } from '../../components/vertical-reel/VerticalReel';
import { Video } from '../../components/video/Video';

export enum BackgroundColor {
  BLUE_GRADIENT = 'Blue Gradient',
  LIGHT_BLUE = 'Light Blue',
  LIGHT_ORANGE = 'Light Orange',
  AUKAKRONUR = 'Aukakronur',
  TRANSPARENT = 'Transparent',
}

export interface HeroProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  backgroundColor?: BackgroundColor;
  image?: Prismic.Image;
  video?: Prismic.Image;
  actions?: React.ReactNode;
  actions2?: React.ReactNode | null;
  overflow?: boolean;
  smallerFont?: boolean;
  isAnimated?: boolean;
  // @todo
  specialHero?: string;
}

const isEmpty = (value: string | React.ReactNode) => {
  if (Children.count(value) === 0) {
    return true;
  }
  return String(value ?? '').trim() === '';
};

const Host = styled.div<{
  backgroundColor: BackgroundColor;
  hasButton: boolean;
  interactive?: boolean;
}>`
  position: relative;
  padding-bottom: 1px;
  margin-bottom: ${(props) => (props.hasButton ? '116px' : '72px')};

  color: ${(props) =>
    props.backgroundColor === BackgroundColor.BLUE_GRADIENT ||
    !props.backgroundColor
      ? props.theme.colors.background
      : props.theme.colors.foreground};

  @media ${device.tablet} {
    padding-top: 28px;
    margin-bottom: 116px;

    ${(props) => (props.interactive ? 'padding-top: 52px;' : '')}
  }
`;

const Container = styled(animated.div)`
  @media ${device.tablet} {
    ${container}
  }
`;

const P = styled.p<{ top?: boolean }>`
  ${typography.p2}
  margin-left:28px;
  letter-spacing: 0.38px;
  max-width: 500px;
  margin-top: ${(props) => (props.top ? '24px' : '0')};
`;

const Aukakronurnar = styled.span`
  display: flex;
  justify-content: center;

  @media ${device.tablet} {
    justify-content: flex-start;
    margin-top: -13px;
    margin-left: 34px;
    margin-bottom: 15px;
  }
`;

const Image = styled.img`
  object-fit: cover;
`.withComponent(Img);

const AspectRatio = styled.div<HeroProps>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;

  margin-bottom: -50px;

  @media ${device.tablet} {
    flex-direction: row-reverse;

    &:before {
      padding-bottom: ${() => (1 / (16 / 9)) * 100}%;
      content: '';
      float: left;
    }

    &:after {
      display: table;
      content: '';
      clear: both;
    }
  }
`;

const Title = styled.h1<HeroProps>`
  ${(props) => (props.smallerFont ? typography.heroSmall : typography.h1)}
  max-width: calc((690 / ${sizes.h2Size}) * 1em);
  color: #fff;
  margin: 0;
  margin-bottom: 36px;

  @media ${device.tablet} {
    ${typography.h2}
    margin-bottom: 30px;
    color: inherit;
  }

  @media ${device.desktop} {
    ${(props) => (props.smallerFont ? typography.heroSmall : typography.h1)}
    margin-bottom: 50px;
  }
`;

const Description = styled.div<{ backgroundColor: string }>`
  font-family: ${(props) => props.theme.font.family};
  font-size: 24px;
  letter-spacing: 0.38px;
  text-align: left;
  line-height: 34px;
  color: #fff;

  @media ${device.tablet} {
    color: inherit;
  }

  div {
    ${typography.p2}

    ul li {
      ${typography.heroListStyle}

      &:before {
        color: ${(props) =>
          props.backgroundColor === BackgroundColor.BLUE_GRADIENT
            ? props.theme.colors.secondary
            : 'currentColor'};
      }
    }
  }
`;

const Background = styled.div<{ backgroundColor: BackgroundColor }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${(props) => {
    switch (props.backgroundColor) {
      case BackgroundColor.BLUE_GRADIENT:
        return `background-image: ${props.theme.gradients.primary};`;
      case BackgroundColor.LIGHT_BLUE:
        return `background-color: ${props.theme.shades.ternary2};`;
      case BackgroundColor.LIGHT_ORANGE:
        return `background-color: #FFE7D5;`;
      case BackgroundColor.AUKAKRONUR:
        return `background: ${props.theme.gradients.aukakronur}`;
    }
  }}
`;

const ImageAspect = styled.div<{ imgOverflow?: boolean }>`
  width: 100%;

  &:before {
    content: '';
    display: block;

    padding-top: 100%;

    @media ${device.tablet} {
      ${(props) => (props.imgOverflow ? 'display: none;' : 'display: block;')}
    }
  }

  > :first-child {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    ${(props) => (props.imgOverflow ? 'max-width: none;' : '')}
  }

  @media ${device.tablet} {
    position: absolute;
    bottom: -31px;
    right: -40px;

    ${(props) => {
      if (props.imgOverflow) {
        return `
        bottom: -64px;
        left: 0;
        width: calc(100% + 50px);
        top: -44px;

        * {
          height: 100%;
        }

        img {
          height: 100%;
          object-fit: contain;
        }
      `;
      }
    }}
  }

  @media ${device.desktop} {
    right: -63px;
    ${(props) => {
      if (props.imgOverflow) {
        return `
        width: calc(100% + 70px);

        img {
          object-fit: cover;
        }
      `;
      }
    }}
  }
`;

const Content = styled.div<HeroProps>`
  position: relative;
  padding: 34px 34px 30px;
  ${container};
  display: flex;
  flex-direction: column;
  ${(props) => {
    switch (props.backgroundColor) {
      case BackgroundColor.LIGHT_BLUE:
        return `background-color: ${props.theme.shades.ternary2};`;
      case BackgroundColor.LIGHT_ORANGE:
        return `background-color: #FFE7D5;`;
      case BackgroundColor.TRANSPARENT:
        return `background-color: transparent;`;
      default:
        return `background-color: ${props.theme.colors.primary};`;
    }
  }}

  @media ${device.mobile} {
    padding: 34px 44px 30px;
  }

  @media ${device.tablet} {
    padding: 115px 0 63px 44px;
    background-color: transparent;
    width: calc(50% + 60px);
  }

  @media ${device.desktop} {
    padding: 115px 0 63px 63px;
  }
`;

const Copy = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<HeroProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-bottom: 56px;

  color: ${(props) =>
    props.backgroundColor === BackgroundColor.BLUE_GRADIENT ||
    !props.backgroundColor
      ? props.theme.colors.background
      : props.theme.colors.foreground};

  @media ${device.tablet} {
    padding-bottom: 36px;
  }
`;

const Actions = styled(animated.div)`
  margin-bottom: -60px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > a {
    margin-top: 20px;
    width: 100%;
  }

  > *:first-child {
    margin-top: 0;
  }

  @media ${device.tablet} {
    margin-bottom: -94px;

    > a {
      margin-top: 30px;
      width: 296px;
    }
  }
`;

const Column = styled.div`
  position: relative;
  width: 100%;

  @media ${device.tablet} {
    width: 51%;
  }
`;

export function SubPageHero(props: HeroProps) {
  const [ref, inView] = useInView(inViewConfig);

  const {
    isAnimated,
    specialHero,
    image,
    video,
    title,
    backgroundColor = BackgroundColor.BLUE_GRADIENT,
    actions,
    actions2,
    description,
    overflow,
    smallerFont,
  } = props;

  const hasButton = !!actions || !!actions2;
  const visible = isAnimated ?? inView;
  const delay = 0;

  const copyStyle = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : 20,
    delay,
  });

  const actionsStyle = useSpring({
    opacity: visible ? 1 : 0,
    overflow: 'hidden',
    delay: delay + 100,
    config: {
      tension: 320,
    },
  });

  const imageStyle = useSpring({
    x: visible ? '0%' : '101%',
  });
  return (
    <Host
      ref={ref}
      backgroundColor={backgroundColor}
      hasButton={hasButton}
      interactive={specialHero === 'aukakronur'}
      id="hero"
    >
      <Container>
        <AspectRatio>
          <Background backgroundColor={backgroundColor} />
          <Column>
            {specialHero === 'aukakronur' ? (
              <VerticalReel />
            ) : (
              <ImageAspect imgOverflow={overflow}>
                <animated.div
                  style={
                    overflow
                      ? actionsStyle
                      : {
                          overflow: 'hidden',
                          transform: imageStyle.x.to(
                            (v) => `translateX(-${v})`,
                          ),
                        }
                  }
                >
                  {video !== undefined && video !== null ? (
                    <Video
                      src={video.url}
                      poster={image?.url}
                      autoPlay={true}
                      loop={true}
                    />
                  ) : (
                    <Image
                      src={image}
                      ar="ar_1_1"
                      isFit={!overflow}
                      containerStyle={imageStyle}
                      percentage={overflow ? 0.6 : 0.5}
                    />
                  )}
                </animated.div>
              </ImageAspect>
            )}
          </Column>

          <Content
            backgroundColor={
              specialHero === 'aukakronur'
                ? BackgroundColor.TRANSPARENT
                : undefined
            }
          >
            <Copy backgroundColor={backgroundColor} style={copyStyle}>
              {specialHero !== 'aukakronur' ? (
                <>
                  {!isEmpty(title) && (
                    <Title image={image} smallerFont={smallerFont}>
                      {title}
                    </Title>
                  )}
                  {!isEmpty(description) && (
                    <Description backgroundColor={backgroundColor}>
                      {description}
                    </Description>
                  )}
                </>
              ) : (
                <>
                  <Aukakronurnar>
                    <Aukakronur />
                  </Aukakronurnar>
                  <P top>Aukakrónur er fríðindakerfi Landsbankans.</P>
                  <P>
                    Þú safnar Aukakrónum þegar þú notar kreditkort tengt
                    Aukakrónusöfnun.
                  </P>
                </>
              )}
            </Copy>

            {!isEmpty(actions || actions2) && (
              <Actions style={actionsStyle}>
                {actions}
                {actions2}
              </Actions>
            )}
          </Content>
        </AspectRatio>
      </Container>
    </Host>
  );
}
